import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Root margin
 * @param {boolean} options.triggerOnce - Only trigger once
 * @returns {[React.RefObject, boolean]} - Ref to attach and visibility state
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible];
};

/**
 * Hook for staggered children animations
 * @param {number} childCount - Number of children to animate
 * @param {number} staggerDelay - Delay between each child (ms)
 * @param {Object} options - IntersectionObserver options
 * @returns {[React.RefObject, boolean[]]} - Ref and array of visibility states
 */
export const useStaggerAnimation = (childCount, staggerDelay = 100, options = {}) => {
  const [ref, isVisible] = useScrollAnimation(options);
  const [visibleItems, setVisibleItems] = useState(Array(childCount).fill(false));

  useEffect(() => {
    if (isVisible) {
      const timeouts = [];
      for (let i = 0; i < childCount; i++) {
        const timeout = setTimeout(() => {
          setVisibleItems(prev => {
            const newState = [...prev];
            newState[i] = true;
            return newState;
          });
        }, i * staggerDelay);
        timeouts.push(timeout);
      }
      return () => timeouts.forEach(t => clearTimeout(t));
    }
  }, [isVisible, childCount, staggerDelay]);

  return [ref, visibleItems, isVisible];
};

export default useScrollAnimation;
